import React, { useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { useActingStoreAdmin } from '../hooks/useActingStoreAdmin';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { integrationApiService, RebatesOnlyApiError } from '../services/integrationApi';
import type {
  IntegrationConfig,
  RebatesOnlyHoldReason,
  RebatesOnlyPreview,
  RebatesOnlyState,
} from '../services/integrationApi';
import { ConfirmDialog, LoadingState, SectionCard, StatusChip } from './ui';

export interface RebatesOnlySectionProps {
  integration: IntegrationConfig;
}

const HOLD_REASONS: RebatesOnlyHoldReason[] = [
  'below_floor',
  'shared_listing',
  'discount_limit',
  'price_review',
  'listing_not_read',
];

/** How many products a confirm dialog lists before it says "and N more". */
const LIST_CAP = 8;

const formatWhen = (value: unknown): string => {
  if (!value) return '';
  const record = value as Record<string, any>;
  const seconds = typeof record.seconds === 'number' ? record.seconds : typeof record._seconds === 'number' ? record._seconds : null;
  const date = seconds != null ? new Date(seconds * 1000) : new Date(String(value));
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

const rebatesOnlyKey = (integrationId: string) => ['rebates-only', integrationId] as const;

/**
 * Motzu manages rebates only (flag `rebate_discount_keep_store_price`): Motzu stops writing the
 * regular price and compare-at on this integration's listings and carries the vendor's instant
 * rebate as a discount off the store's own price. Turning it on is confirmed against a preview of
 * what will change. Shopify and Lightspeed Retail work; other platforms show why they cannot.
 */
const RebatesOnlySection: React.FC<RebatesOnlySectionProps> = ({ integration }) => {
  const { t } = useAppTranslation();
  const { enabled: flagOn } = useOrgFeatureFlag('rebate_discount_keep_store_price');
  const { canManage } = useActingStoreAdmin();
  const { formatCurrency } = useLocale();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [confirmOn, setConfirmOn] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const key = rebatesOnlyKey(integration.id);
  const stateQuery = useQuery<RebatesOnlyState>({
    queryKey: key,
    queryFn: () => integrationApiService.getRebatesOnly(integration.id),
    enabled: flagOn,
  });

  const available = stateQuery.data != null && !stateQuery.data.unavailableReason;
  const isOn = stateQuery.data?.enabled === true;

  // The preview reads the shop and can take a few seconds, so it loads only once the store asks.
  const previewQuery = useQuery<RebatesOnlyPreview>({
    queryKey: [...key, 'preview'],
    queryFn: () => integrationApiService.previewRebatesOnly(integration.id),
    enabled: flagOn && confirmOn && available && !isOn,
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });

  const failureMessage = (err: unknown): string =>
    err instanceof RebatesOnlyApiError && err.status === 403
      ? t('integrations.rebatesOnly.ownerOnly')
      : err instanceof Error && err.message
        ? err.message
        : t('integrations.rebatesOnly.failed');

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: key });
    queryClient.invalidateQueries({ queryKey: ['integrations'] });
  };

  const enable = useMutation({
    mutationFn: () => integrationApiService.enableRebatesOnly(integration.id),
    onSuccess: (result) => {
      setConfirmOn(false);
      setError(null);
      showToast(
        result.runQueued ? t('integrations.rebatesOnly.enabledQueued') : t('integrations.rebatesOnly.enabledNextSync'),
        'success',
      );
      refresh();
    },
    onError: (err) => {
      setConfirmOn(false);
      setError(failureMessage(err));
    },
  });

  const disable = useMutation({
    mutationFn: () => integrationApiService.disableRebatesOnly(integration.id),
    onSuccess: () => {
      setConfirmOff(false);
      setError(null);
      showToast(t('integrations.rebatesOnly.disabled'), 'success');
      refresh();
    },
    onError: (err) => {
      setConfirmOff(false);
      setError(failureMessage(err));
    },
  });

  if (!flagOn) return null;

  const platformNote =
    integration.platform === 'shopify'
      ? t('integrations.rebatesOnly.shopifyNote')
      : integration.platform === 'lightspeed'
        ? t('integrations.rebatesOnly.lightspeedNote')
        : null;

  const unavailableReason = stateQuery.data?.unavailableReason ?? null;
  const status = stateQuery.data?.status ?? null;
  const preview = previewQuery.data;
  const heldTotal = (held: Partial<Record<RebatesOnlyHoldReason, number>> | undefined) =>
    HOLD_REASONS.reduce((sum, reason) => sum + (held?.[reason] ?? 0), 0);

  const moreLine = (shown: number, count: number) =>
    count > shown ? (
      <Typography variant="caption" color="text.secondary">
        {t('integrations.rebatesOnly.andMore', { count: count - shown })}
      </Typography>
    ) : null;

  const previewBody = (() => {
    if (previewQuery.isLoading) return <LoadingState />;
    if (previewQuery.isError) {
      return <Alert severity="error">{failureMessage(previewQuery.error)}</Alert>;
    }
    if (!preview) return null;
    const raisedShown = preview.raises.products.slice(0, LIST_CAP);
    const belowShown = preview.storeSetBelowMap.products.slice(0, LIST_CAP);
    return (
      <Stack spacing={2} sx={{ mt: 2 }}>
        {preview.reconnectRequired && (
          <Alert severity="warning">{t('integrations.rebatesOnly.reconnectFirst')}</Alert>
        )}
        <Box>
          <Typography variant="subtitle2">
            {t('integrations.rebatesOnly.willDiscount', { count: preview.discounted })}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2">
            {t('integrations.rebatesOnly.willNotDiscount', { count: heldTotal(preview.held) })}
          </Typography>
          {HOLD_REASONS.filter((reason) => (preview.held[reason] ?? 0) > 0).map((reason) => (
            <Typography key={reason} variant="body2" color="text.secondary">
              {t('integrations.rebatesOnly.heldLine', {
                count: preview.held[reason],
                reason: t(`integrations.rebatesOnly.reason.${reason}`),
              })}
            </Typography>
          ))}
        </Box>
        <Box>
          <Typography variant="subtitle2">
            {t('integrations.rebatesOnly.willRaise', { count: preview.raises.count })}
          </Typography>
          {preview.raises.count > 0 && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('integrations.rebatesOnly.willRaiseHelp')}
              </Typography>
              {raisedShown.map((p) => (
                <Typography key={p.productId} variant="body2">
                  {t('integrations.rebatesOnly.raiseRow', {
                    name: p.name,
                    from: formatCurrency(p.from),
                    to: formatCurrency(p.to),
                  })}
                </Typography>
              ))}
              {moreLine(raisedShown.length, preview.raises.count)}
            </>
          )}
        </Box>
        <Box>
          <Typography variant="subtitle2">
            {t('integrations.rebatesOnly.belowMap', { count: preview.storeSetBelowMap.count })}
          </Typography>
          {preview.storeSetBelowMap.count > 0 && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('integrations.rebatesOnly.belowMapHelp')}
              </Typography>
              {belowShown.map((p) => (
                <Typography key={p.productId} variant="body2">
                  {t('integrations.rebatesOnly.belowMapRow', {
                    name: p.name,
                    price: formatCurrency(p.livePrice),
                    map: formatCurrency(p.map),
                  })}
                </Typography>
              ))}
              {moreLine(belowShown.length, preview.storeSetBelowMap.count)}
            </>
          )}
        </Box>
      </Stack>
    );
  })();

  const heldRows = (status?.heldProducts ?? []).slice(0, LIST_CAP);

  return (
    <SectionCard
      title={t('integrations.rebatesOnly.title')}
      subtitle={t('integrations.rebatesOnly.subtitle')}
      actions={isOn ? <StatusChip label={t('integrations.rebatesOnly.on')} tone="positive" /> : undefined}
    >
      <Stack spacing={1.5}>
        {stateQuery.isLoading && <LoadingState />}
        {stateQuery.isError && <Alert severity="error">{failureMessage(stateQuery.error)}</Alert>}
        {unavailableReason && (
          <Alert severity="info">{t(`integrations.rebatesOnly.unavailable.${unavailableReason}`)}</Alert>
        )}
        {available && platformNote && (
          <Typography variant="body2" color="text.secondary">
            {platformNote}
          </Typography>
        )}
        {error && <Alert severity="error">{error}</Alert>}

        {isOn && (
          <>
            <Typography variant="body2" color="text.secondary">
              {status
                ? t('integrations.rebatesOnly.last', {
                    when: formatWhen(status.at),
                    discounted: status.discounted,
                    held: heldTotal(status.held),
                    raised: status.raised,
                  })
                : t('integrations.rebatesOnly.never')}
            </Typography>
            {status && status.raiseFailed > 0 && (
              <Alert severity="warning">{t('integrations.rebatesOnly.raiseFailed', { count: status.raiseFailed })}</Alert>
            )}
            {status && status.storeSetBelowMap > 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('integrations.rebatesOnly.storeBelowMapNow', { count: status.storeSetBelowMap })}
              </Typography>
            )}
            {status && status.capSkipped > 0 && (
              <Alert severity="warning">{t('integrations.rebatesOnly.cap', { count: status.capSkipped })}</Alert>
            )}
            {heldRows.length > 0 && (
              <Box>
                <Typography variant="subtitle2">{t('integrations.rebatesOnly.heldTitle')}</Typography>
                {heldRows.map((row) => (
                  <Typography key={`${row.productId}-${row.reason}`} variant="body2">
                    {row.name}
                    <Typography component="span" variant="body2" color="text.secondary">
                      {` - ${t(`integrations.rebatesOnly.reason.${row.reason}`)}`}
                    </Typography>
                  </Typography>
                ))}
                {moreLine(heldRows.length, status?.heldProducts.length ?? 0)}
              </Box>
            )}
          </>
        )}

        <Box>
          {isOn ? (
            <Button size="small" variant="outlined" onClick={() => setConfirmOff(true)} disabled={!canManage || disable.isPending}>
              {t('integrations.rebatesOnly.turnOff')}
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setError(null);
                setConfirmOn(true);
              }}
              disabled={!available || !canManage || enable.isPending}
            >
              {t('integrations.rebatesOnly.turnOn')}
            </Button>
          )}
          {!canManage && available && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('integrations.rebatesOnly.ownerOnly')}
            </Typography>
          )}
        </Box>
      </Stack>

      <ConfirmDialog
        open={confirmOn}
        title={t('integrations.rebatesOnly.confirmOnTitle')}
        message={t('integrations.rebatesOnly.confirmOnMessage')}
        preview={previewBody}
        confirmLabel={t('integrations.rebatesOnly.turnOn')}
        confirmDisabled={!preview || previewQuery.isError || preview.reconnectRequired}
        loading={enable.isPending}
        maxWidth="sm"
        onConfirm={() => enable.mutate()}
        onClose={() => setConfirmOn(false)}
      />
      <ConfirmDialog
        open={confirmOff}
        title={t('integrations.rebatesOnly.confirmOffTitle')}
        message={t('integrations.rebatesOnly.confirmOffMessage')}
        confirmLabel={t('integrations.rebatesOnly.turnOff')}
        confirmColor="warning"
        loading={disable.isPending}
        onConfirm={() => disable.mutate()}
        onClose={() => setConfirmOff(false)}
      />
    </SectionCard>
  );
};

export default RebatesOnlySection;
